import { AirflowDashboardHandler as RealHandler } from './airflow-dashboard-handler.real';
import { AirflowDashboardHandler as MockHandler } from './airflow-dashboard-handler.mock';

/*
 * Every endpoint is now live against Apache Airflow's real REST API v1 (2026-08-12), with one
 * exception: pushDagSource hits the platform gateway (REACT_APP_PLATFORM_URL/airflow/api/v1/
 * dags/push), not Airflow's own webserver, since Airflow's REST API has no write endpoint for
 * DAG source. XCom writes (createXcomEntry/updateXcomEntry/deleteXcomEntry — v1 is GET-only in
 * this version) have no backing endpoint in Airflow 2.6.0 specifically and will 404/405 visibly
 * rather than silently mock — by direction, that's preferred over hiding the gap. Backfills,
 * the Audit Log (listEventLogs), and Rendered Templates (getRenderedFields) had the same
 * "no real server-side support" problem but were removed from the UI entirely instead — see
 * API_GAPS.md. See airflow-dashboard-handler.real.ts for per-method notes. MockHandler is
 * kept as a fallback spread and for local dev without backend access — flip a method back by
 * removing it from LIVE_METHODS.
 */
const LIVE_METHODS: (keyof typeof RealHandler)[] = [
    'listDags',
    'getDag',
    'getDagDetails',
    'deleteDag',
    'patchDag',
    'triggerDag',
    'getDagTask',
    'listDagTasks',
    'listDagTags',
    'listImportErrors',
    'getDagSource',
    'listDagRuns',
    'patchDagRun',
    'deleteDagRun',
    'getDagRun',
    'getDagCalendar',
    'clearDagRun',
    'listDagTaskInstances',
    'listRunTaskInstances',
    'clearTaskInstances',
    'getTaskLogs',
    'getRunTaskInstance',
    'getTaskInstanceLinks',
    'patchTaskInstance',
    'deleteTaskInstance',
    'listXcomEntries',
    'createXcomEntry',
    'updateXcomEntry',
    'deleteXcomEntry',
    'listAssetEvents',
    'pushDagSource',
];

const liveOverrides = Object.fromEntries(LIVE_METHODS.map((method) => [method, RealHandler[method]]));

// Typed against RealHandler (not MockHandler) since the UI is written against the real
// handler's complete parameter signatures — safe at runtime even for methods where the mock's
// own implementation ignores params it doesn't model (JS simply drops unused arguments/fields).
export const AirflowDashboardHandler = {
    ...MockHandler,
    ...liveOverrides,
} as typeof RealHandler;
